/**
 * SRD Strategy View
 * 
 * Sovereign Resilience Derivative strategy generation.
 */

import React, { useState } from 'react';
import { TrendingUp, Loader2, Globe, Leaf, DollarSign, Shield } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function SRDStrategy({ apiUrl }) {
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [strategy, setStrategy] = useState(null);

  const eligibleCountries = [
    'Barbados', 'Jamaica', 'Trinidad and Tobago', 'Fiji', 'Samoa', 'Tonga',
    'Vanuatu', 'Maldives', 'Seychelles', 'Mauritius', 'Cape Verde',
    'Belize', 'Guyana', 'Suriname'
  ];

  async function generateStrategy() {
    if (!country) return;
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/srd/strategy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ country })
      });
      const data = await response.json();
      setStrategy(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">SRD Strategy Generator</h1>
        <p className="text-slate-400">Sovereign Resilience Derivative implementation planning</p>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-xl p-6 border border-cyan-500/30">
        <h2 className="text-lg font-semibold mb-2">What is the SRD?</h2>
        <p className="text-slate-300">
          The Sovereign Resilience Derivative is an innovative development finance mechanism where 
          stronger nations guarantee bonds for developing countries to build renewable energy 
          infrastructure. Energy savings flow into sovereign wealth funds that become self-sustaining, 
          creating perpetual national wealth while countering predatory lending practices.
        </p>
      </div>

      {/* Country Selection */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h2 className="text-lg font-semibold mb-4">Select Target Country</h2>
        <div className="flex space-x-4">
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-4 py-3"
          >
            <option value="">Select country...</option>
            {eligibleCountries.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <button
            onClick={generateStrategy}
            disabled={loading || !country}
            className="px-6 py-3 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium disabled:opacity-50 flex items-center"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <TrendingUp className="w-5 h-5 mr-2" />}
            Generate Strategy
          </button>
        </div>
      </div>

      {/* Results */}
      {strategy && (
        <div className="space-y-6">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center text-slate-400 mb-2">
                <Leaf className="w-5 h-5 mr-2 text-green-500" />
                Climate Vulnerability
              </div>
              <div className="text-2xl font-bold text-green-400">
                {strategy.climateVulnerability?.level || 'High'}
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center text-slate-400 mb-2">
                <DollarSign className="w-5 h-5 mr-2 text-yellow-500" />
                Bond Size
              </div>
              <div className="text-2xl font-bold text-yellow-400">
                ${strategy.bondStructure?.totalSize || 'TBD'}M
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center text-slate-400 mb-2">
                <Globe className="w-5 h-5 mr-2 text-cyan-500" />
                Counter-BRI
              </div>
              <div className="text-2xl font-bold text-cyan-400">
                {strategy.counterBRIAlignment?.aligned ? 'Aligned' : 'Partial'}
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center text-slate-400 mb-2">
                <Shield className="w-5 h-5 mr-2 text-purple-500" />
                Implementation
              </div>
              <div className="text-2xl font-bold text-purple-400">
                {strategy.implementationPlan?.phases?.length || 4} Phases
              </div>
            </div>
          </div>

          {/* SWF Projection Chart */}
          {strategy.swfProjection?.projection && (
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold mb-4">Sovereign Wealth Fund Projection (30 Years)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={strategy.swfProjection.projection}>
                  <XAxis dataKey="year" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1e293b', 
                      border: '1px solid #334155',
                      borderRadius: '8px'
                    }} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#06b6d4" 
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Implementation Plan */}
          {strategy.implementationPlan?.phases && (
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold mb-4">Implementation Roadmap</h3>
              <div className="space-y-4">
                {strategy.implementationPlan.phases.map((phase, i) => (
                  <div key={i} className="flex items-start">
                    <div className="w-10 h-10 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center font-bold mr-4">
                      {phase.phase}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold">{phase.name}</div>
                      <div className="text-sm text-slate-400">{phase.duration}</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {phase.activities?.map((activity, j) => (
                          <span key={j} className="px-2 py-1 bg-slate-700 rounded text-xs">
                            {activity}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Raw Data */}
          <details className="bg-slate-800 rounded-xl border border-slate-700">
            <summary className="p-4 cursor-pointer hover:bg-slate-700/50">
              View Full Strategy Data
            </summary>
            <pre className="p-4 text-xs overflow-auto max-h-96 bg-slate-900">
              {JSON.stringify(strategy, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}
